import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { ApiError, getFieldViolations } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  VocabularyTermPicker,
  type VocabularyTermValue,
} from '@/components/vocabulary/VocabularyTermPicker';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useDiagnosisMutations } from '@/features/patient-record/hooks/useDiagnosisMutations';
import { diagnosisStatusToI18nKey } from '@/lib/clinicalBadges';
import type {
  DiagnosisStatus,
  DiagnosisVocabulary,
} from '@/features/patient-record/types';

import { FormField } from './_shared/FormField';

const DIAGNOSIS_STATUSES: readonly DiagnosisStatus[] = [
  'Confirmed',
  'Suspected',
  'Ruled Out',
  'Molecular Diagnosis',
];

const DIAGNOSIS_VOCABULARIES: readonly DiagnosisVocabulary[] = ['omim', 'ordo'];

const NOTES_MAX = 16_000;
const AGE_MAX = 100;

interface AddDiagnosisDialogProps {
  readonly guid: string;
  readonly onClose: () => void;
}

interface DraftState {
  vocabulary: DiagnosisVocabulary;
  term: VocabularyTermValue | null;
  status: DiagnosisStatus;
  ageAtDiagnosis: string;
  notes: string;
}

const INITIAL_DRAFT: DraftState = {
  vocabulary: 'omim',
  term: null,
  status: 'Suspected',
  ageAtDiagnosis: '',
  notes: '',
};

/**
 * Add-diagnosis dialog. Picks a vocabulary (OMIM or Orphanet) and then a term
 * from it via the shared {@link VocabularyTermPicker} (PTV2-1042/PTV2-1606),
 * then captures clinician status (default ``Suspected``), an optional
 * free-text age at diagnosis, and optional notes. Editing an existing
 * diagnosis stays inline in `DiagnosesSection` — only Add lives here because
 * the term is fixed once recorded (wrong term ⇒ delete + re-add).
 *
 * Changing the vocabulary clears the selected term. The picker only ever
 * returns ids from the vocabulary it is pointed at, so this is what keeps the
 * pair consistent: without the reset an OMIM term could be submitted under
 * ``ordo`` and the backend would (correctly) reject it with a 422.
 */
export function AddDiagnosisDialog({
  guid,
  onClose,
}: AddDiagnosisDialogProps): ReactElement {
  const { t } = useTranslation();
  const { add } = useDiagnosisMutations(guid);

  // Commit only — never closes (the primitive's onSave + the confirm-prompt Save).
  const commit = (d: DraftState): Promise<unknown> => {
    if (d.term === null) return Promise.resolve();
    const ageTrim = d.ageAtDiagnosis.trim();
    const notesTrim = d.notes.trim();
    return add.mutateAsync({
      termId: d.term.id,
      vocabulary: d.vocabulary,
      title: d.term.label,
      status: d.status,
      ageAtDiagnosis: ageTrim.length > 0 ? ageTrim : null,
      notes: notesTrim.length > 0 ? notesTrim : null,
    });
  };

  // PTV2-1327: one primitive owns the draft. Add-diagnosis is explicit-submit, so
  // autosave is off; the primitive registers while dirty so a refresh prompts.
  const form = useEditableDraft<DraftState>({
    initial: INITIAL_DRAFT,
    label: t('patient.diagnoses.add.title'),
    autosave: false,
    canSave: (d) =>
      d.term !== null && d.ageAtDiagnosis.length <= AGE_MAX && d.notes.length <= NOTES_MAX,
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  // Explicit submit: commit AND close.
  const submitAndClose = (): void => {
    if (!form.canSave) return;
    void commit(draft).then(onClose).catch(() => undefined);
  };

  const error = add.error;
  const isConflict = error instanceof ApiError && error.code === 'conflict';
  const fieldViolations = getFieldViolations(error);
  const hasGenericError = error !== null && !isConflict && fieldViolations.length === 0;

  // PTV2-1301: guard dismiss (outside-click / Escape / X / refresh) when the form has
  // input. The primitive already registers, so the guard only intercepts dismissal.
  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: add.isPending,
    label: t('patient.diagnoses.add.title'),
    onSave: submitAndClose,
    onClose,
    register: false,
  });

  return (
    <>
    <Dialog open {...guard.rootProps}>
      <DialogContent className="max-w-md" {...guard.contentProps}>
        <DialogHeader>
          <DialogTitle>{t('patient.diagnoses.add.title')}</DialogTitle>
        </DialogHeader>
        <form
          ref={form.containerProps.ref}
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            submitAndClose();
          }}
        >
          <FormField label={t('patient.diagnoses.add.vocabularyLabel')} required>
            <Select
              value={draft.vocabulary}
              onChange={(event) => {
                const next = event.target.value as DiagnosisVocabulary;
                // Clearing the term is load-bearing, not tidiness — see the class comment.
                form.setDraft((d) => ({ ...d, vocabulary: next, term: null }));
              }}
              className="h-8 text-sm"
              aria-label={t('patient.diagnoses.add.vocabularyLabel')}
            >
              {DIAGNOSIS_VOCABULARIES.map((vocabulary) => (
                <option key={vocabulary} value={vocabulary}>
                  {t(`patient.diagnoses.vocabulary.${vocabulary}`)}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label={t('patient.diagnoses.add.termLabel')} required>
            <VocabularyTermPicker
              // Remount on vocabulary change so the picker drops its own internal
              // query state and starts clean against the new vocabulary.
              key={draft.vocabulary}
              vocabularyId={draft.vocabulary}
              value={draft.term}
              onChange={(next) => {
                form.setDraft((d) => ({ ...d, term: next }));
              }}
              placeholder={t('patient.diagnoses.add.termPlaceholder', {
                vocabulary: t(`patient.diagnoses.vocabulary.${draft.vocabulary}`),
              })}
              ariaLabel={t('patient.diagnoses.add.termLabel')}
              showInfo
            />
          </FormField>

          <FormField label={t('patient.diagnoses.fields.status')}>
            <Select
              value={draft.status}
              onChange={(event) => {
                form.setDraft((d) => ({
                  ...d,
                  status: event.target.value as DiagnosisStatus,
                }));
              }}
              className="h-8 text-sm"
            >
              {DIAGNOSIS_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`patient.diagnoses.status.${diagnosisStatusToI18nKey(status)}`)}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label={t('patient.diagnoses.fields.ageAtDiagnosisLabel')}>
            <Input
              value={draft.ageAtDiagnosis}
              placeholder={t('patient.diagnoses.fields.agePlaceholder')}
              maxLength={AGE_MAX}
              onChange={(event) => {
                form.setDraft((d) => ({ ...d, ageAtDiagnosis: event.target.value }));
              }}
              className="h-8 text-sm"
            />
          </FormField>

          <FormField label={t('patient.diagnoses.fields.notesLabel')}>
            <Textarea
              value={draft.notes}
              placeholder={t('patient.diagnoses.fields.notesPlaceholder')}
              maxLength={NOTES_MAX}
              rows={3}
              onChange={(event) => {
                form.setDraft((d) => ({ ...d, notes: event.target.value }));
              }}
              className="text-sm"
            />
          </FormField>

          {isConflict && (
            <ErrorCallout message={t('patient.diagnoses.add.errors.duplicate')} />
          )}
          {fieldViolations.length > 0 && (
            <ErrorCallout
              message={t('patient.diagnoses.add.errors.validation')}
              details={fieldViolations.map((v) => `${v.field}: ${v.message}`)}
            />
          )}
          {hasGenericError && <ErrorCallout message={t('app.unknownError')} />}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={guard.requestClose}
              disabled={add.isPending}
            >
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!form.canSave || add.isPending}
              loading={add.isPending}
              loadingText={t('patient.dirtyFooter.saving')}
            >
              {t('patient.diagnoses.add.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    {guard.confirm}
    </>
  );
}

interface ErrorCalloutProps {
  readonly message: string;
  readonly details?: readonly string[];
}

function ErrorCallout({ message, details }: ErrorCalloutProps): ReactElement {
  return (
    <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3">
      <AlertTriangle
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
        aria-hidden="true"
      />
      <div className="space-y-1">
        <p className="text-xs text-destructive">{message}</p>
        {details !== undefined && details.length > 0 && (
          <ul className="text-xs text-destructive/90 list-disc pl-4 space-y-0.5">
            {details.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
